var productName = 'calendar';import EventRenderer from './mixin/EventRenderer.js';
import SchedulerDatePicker from '../../Scheduler/widget/SchedulerDatePicker.js';
import DateHelper from '../../Core/helper/DateHelper.js';
import DomHelper from '../../Core/helper/DomHelper.js';
/**
 * @module Calendar/widget/CalendarDatePicker
 */
/**
 * A subclass of {@link Core.widget.DatePicker} which is able to show the presence of 
 * events in its cells if contained in a Calendar, and {@link #config-showEvents} is set
 * to a truthy value.
 *
 * The `datepicker` Widget type is implemented by this class when this class is imported, or built
 * into a bundle, and so any {@link Core.widget.DateField} may have its
 * {@link Core.widget.PickerField#config-picker} configured to use its capabilities of showing
 * the presence of events in its date cells.
 *
 * @extends Scheduler/widget/SchedulerDatePicker
 * @mixes Calendar/widget/mixin/EventRenderer
 * @classType datepicker
 */
export default class CalendarDatePicker extends SchedulerDatePicker.mixin(EventRenderer) {
    static $name = 'CalendarDatePicker';
    // Factoryable type name
    static type = 'datepicker';
    static configurable = {
        /**
         * How to show presence of events in the configured {@link #config-eventStore} in the
         * day cells. Values may be:
         *
         * * `false` - Do not show events in cells.
         * * `true` - Show a themeable bullet to indicate the presence of events for a date.
         * * `'count'` - Show a themeable badge containing the event count for a date.
         * * `'dots'` - Show small event-coloured bullets up to a maximum of four to indicate event presence.
         * @config {Boolean|'count'|'dots'}
         * @default false
         */
        showEvents : null,
        alwaysRefreshOnMonthChange : true
    }
    construct(config) {
        if (!config.eventStore) {
            const calendar = (config.owner || config.parent)?.up('calendar');
            calendar && (config.eventStore = calendar.eventStore);
        }
        super.construct(...arguments);
    }
    refreshEventsMap() {
        const me = this;
        if (me.showEvents) {
            me.eventsMap = me.eventStore[me.showEvents === 'dots' ? 'getEvents' : 'getEventCounts']({
                startDate : me.startDate,
                endDate   : me.endDate,
                dateMap   : me.eventsMap || (me.eventsMap = new Map())
            });
        }
    }
    cellRenderer({ cell, date }) {
        const
            { showEvents } = this,
            eventInfo      = this.eventsMap?.get?.(DateHelper.makeKey(date)),
            count          = showEvents === 'dots' ? eventInfo?.length : eventInfo,
            countCls       = showEvents === 'count' ? 'b-cell-events-badge' : showEvents === 'dots' ? 'b-cal-minimal-event-container' : 'b-icon b-icon-circle';
        if (showEvents === 'dots') {
            // We replace the text-only date with an element so it can carry the background
            // and the background will not obscure the dots.
            cell.innerHTML = '';
            DomHelper.createElement({
                class  : 'b-date-cell-inner',
                text   : date.getDate(),
                parent : cell
            });
        }
        if (count) {
            const cellContent = {
                dataset : {
                    count
                },
                class : {
                    [countCls]                                      : 1,
                    [SchedulerDatePicker.getEventCountClass(count)] : 1
                },
                parent                                 : cell,
                [showEvents === 'count' ? 'text' : ''] : count
            };
            if (showEvents === 'dots') {
                const children = cellContent.children = [];
                for (let i = 0; i < Math.min(eventInfo?.length || 0, 4); i++) {
                    const eventDomConfig = this.createEventDomConfig({
                        eventRecord : eventInfo[i],
                        // renders without content with just background colour
                        minimal : true
                    });
                    children.push(eventDomConfig);
                }
            }
            DomHelper.createElement(cellContent);
        }
    }
    static setupClass(meta) {
        // We take over the type name 'datepicker' when we are in the app
        meta.replaceType = true;
        super.setupClass(meta);
    }
}
CalendarDatePicker.initClass();
CalendarDatePicker._$name = 'CalendarDatePicker';